/**
 * Thrivia Embed SDK: the button surface.
 *
 * Purpose: turn a mount into one thing that opens checkout. There are two shapes of that and they
 * are treated completely differently, which is the whole content of this file:
 *
 * 1. **Our mount div** (`<div data-thrivia-event="..." data-thrivia-mode="button">`, the shape the
 *    generated snippet uses): we draw the coral pill inside it, reusing the card's own `.thrivia-c__a`
 *    class so there is exactly one button design in the bundle.
 * 2. **The organizer's own CTA**, tagged in place: a legacy `data-thrivia-action="checkout"` anchor,
 *    an autolinked link, or any `<a>`/`<button>` they put the mount attributes on. **We render
 *    NOTHING.** Their element keeps its markup, its children and its classes; the only trace we
 *    leave is the `data-thrivia-bound` stamp (`scan.ts`, and it is the shim's handover token) plus
 *    the `data-thrivia-state` attribute every mount carries. Binding their CTA in place is the
 *    entire point of that shape: a site whose button already matches their brand does not want ours,
 *    and restyling it would be the single most visible way this SDK could damage somebody's page.
 *
 * The rule for telling them apart: a mount is THEIRS when it was matched by the legacy selector,
 * upgraded by autolink, or is itself an interactive element (`A` or `BUTTON`). Everything else is a
 * container we were given to fill. It is a structural test rather than a heuristic about their
 * classes, and it is asserted in both directions.
 *
 * What the pill says: "Book now" while the event is live, "View event" in every other state. The
 * card surface renders four distinct states because it has a whole box to explain itself in; a
 * button has one line of text and no room, so state presentation belongs to the checkout frame,
 * which is opened for every state and knows all of them. A pill that said "Sold out" and then
 * opened checkout anyway would be the worse of the two designs.
 *
 * The pill is a real `<a href="card.url">`. Our click handler intercepts it (`index.ts`), so the
 * href is never followed on a healthy page; it is there so a visitor whose overlay JS fails still
 * has a working link, which is the same progressive-enhancement rule the card follows.
 *
 * No runtime dependencies, DOM only, ES2019 output target.
 */

import type { MountRecord, MountRenderer } from "../scan";
import { injectStyles } from "../styles";
import { C, HTTP_RE, OWN, e, linkTo, swap } from "./card";

/** Interactive elements an organizer tags in place. Never written into. */
const THEIRS_RE = /^(?:A|BUTTON)$/;

/**
 * The pill, and the skeleton that reserves its box.
 *
 * `.thrivia-c__a` is the card's own CTA class, reused rather than copied: one button design, one
 * set of contrast numbers to keep honest. `.thrivia-b` drops the card gutters for the standalone
 * case. `OWN` is both the reset scope and `clearOwn`'s marker, so it must be on the root or the
 * failure posture cannot clean up after itself.
 */
const PILL = OWN + " " + C + "a thrivia-b";

/** True when the mount element belongs to the organizer rather than to us. */
export function isTheirs(record: MountRecord): boolean {
  return record.legacy || record.autolink || THEIRS_RE.test(record.el.tagName);
}

/**
 * One frame of a button mount.
 *
 * `pending` and `loading` paint the same 44px pill in grey, so the box the skeleton reserves and
 * the box the button occupies are the same box and the organizer's page does not move when the data
 * lands. `error` and `missing` render nothing, which hands the element back holding exactly the
 * markup the organizer wrote, baked permalink anchor and all.
 */
function paint(record: MountRecord): void {
  if (isTheirs(record)) return;

  const state = record.state;
  const card = record.card;
  let node: HTMLElement | null = null;

  if (state === "ready" && card) {
    injectStyles();
    node = e("a", PILL, null, card.state === "live" ? "Book now" : "View event");
    if (HTTP_RE.test(card.url)) linkTo(node, card.url);
  } else if (state === "pending" || state === "loading") {
    injectStyles();
    node = e("div", PILL + " " + C + "bar", null);
  }

  swap(record.el, node);
}

/**
 * The button renderer, registered for mode `button` in `index.ts`.
 *
 * No `teardown`: nothing here attaches outside `record.el`. The click handler belongs to `scan.ts`
 * and lives on the element itself, so a pruned mount takes it with it.
 */
export const buttonRenderer: MountRenderer = { mount: paint, update: paint };
